import { useMemo, useState } from "react";
import { useData } from "@/context/DataContext";
import { addDays, isoDate, parseISO, startOfWeek, WEEKDAY_SHORT, formatTime, toMinutes } from "@/lib/date";
import { Button } from "@/components/ui/button";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import TaskList from "@/components/TaskList";

export default function WeekPage() {
  const { events, tasks, catById, importantDates } = useData();
  const [anchor, setAnchor] = useState(new Date());

  const weekStart = startOfWeek(anchor);
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  const dayEvents = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[isoDate(d)] = []));
    events.forEach((e) => {
      if (map[e.date]) map[e.date].push(e);
    });
    Object.values(map).forEach((arr) =>
      arr.sort((a, b) => toMinutes(a.start_time) - toMinutes(b.start_time))
    );
    return map;
  }, [events, days]);

  const importantByDay = useMemo(() => {
    const map = {};
    days.forEach((d) => (map[isoDate(d)] = []));
    importantDates.forEach((i) => {
      if (map[i.date]) map[i.date].push(i);
    });
    return map;
  }, [importantDates, days]);

  const weekTasks = useMemo(() => {
    const dates = days.map((d) => isoDate(d));
    return tasks.filter((t) => dates.includes(t.date));
  }, [tasks, days]);

  const rangeLabel = `${weekStart.toLocaleDateString("nl-NL", { day: "numeric", month: "short" })} – ${addDays(weekStart, 6).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" })}`;

  const weekOffset = Math.round((weekStart - startOfWeek(new Date())) / (7 * 86400000));
  const headingLabel = weekOffset === 0 ? "Deze week" : weekOffset === 1 ? "Volgende week" : weekOffset === -1 ? "Vorige week" : (weekOffset > 0 ? `Over ${weekOffset} weken` : `${Math.abs(weekOffset)} weken geleden`);

  return (
    <div className="p-5 lg:p-8">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500">Weekoverzicht</div>
          <h1 className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-50 mt-1">
            {headingLabel}
          </h1>
          <div className="text-sm text-slate-500 mt-1">{rangeLabel}</div>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="week-prev" variant="outline" size="icon"
            onClick={() => setAnchor(addDays(anchor, -7))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretLeft size={16} />
          </Button>
          <Button data-testid="week-today" variant="outline"
            onClick={() => setAnchor(new Date())} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-200 text-xs">
            Deze week
          </Button>
          <Button data-testid="week-next" variant="outline" size="icon"
            onClick={() => setAnchor(addDays(anchor, 7))} className="border-slate-800 bg-[#121C2F] hover:bg-slate-800 text-slate-300">
            <CaretRight size={16} />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-9">
          <div className="grid grid-cols-1 md:grid-cols-7 gap-3">
            {days.map((d, idx) => {
              const iso = isoDate(d);
              const isToday = iso === isoDate(new Date());
              const dayEvs = dayEvents[iso] || [];
              const dayImp = importantByDay[iso] || [];
              return (
                <div
                  key={iso}
                  className={`rounded-xl border p-3 min-h-[220px] flex flex-col ${
                    isToday ? "border-blue-500/40 bg-blue-500/5" : "border-slate-800 bg-[#121C2F]"
                  }`}
                >
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500">{WEEKDAY_SHORT[idx]}</div>
                      <div className={`text-lg font-semibold ${isToday ? "text-blue-300" : "text-slate-100"}`}>
                        {d.getDate()}
                      </div>
                    </div>
                    {dayEvs.length > 0 && (
                      <div className="text-[10px] text-slate-500">{dayEvs.length} evt</div>
                    )}
                  </div>
                  <div className="space-y-1.5 flex-1 overflow-y-auto max-h-64">
                    {dayImp.map((i) => (
                      <div
                        key={i.id}
                        className="text-[11px] px-2 py-1 rounded border border-rose-500/30 bg-rose-500/10 text-rose-300 font-medium"
                      >
                        ★ {i.title}
                      </div>
                    ))}
                    {dayEvs.map((e) => {
                      const cat = catById(e.category_id);
                      const color = cat?.color || "#3B82F6";
                      return (
                        <div
                          key={e.id}
                          className="rounded-md px-2 py-1.5 text-[11px] leading-tight"
                          style={{
                            background: `${color}18`,
                            borderLeft: `2px solid ${color}`,
                          }}
                        >
                          <div className="text-slate-100 font-medium truncate">{e.title}</div>
                          <div className="text-slate-400 mt-0.5">{formatTime(e.start_time)}</div>
                        </div>
                      );
                    })}
                    {dayEvs.length === 0 && dayImp.length === 0 && (
                      <div className="text-[11px] text-slate-600 italic pt-2">Vrije dag</div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className="lg:col-span-3">
          <TaskList tasks={weekTasks} title="Weektaken" emptyText="Geen taken deze week." />
        </div>
      </div>
    </div>
  );
}
